// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Spinner } from '@loa/ui-components';
import { clientListApi } from '../api/clientListApi';
import { profileFormApi } from '../api/profileFormApi';
import type { FactFindSchema, ProfileFormVersion } from '../api/profileFormApi';
import type { ClientDetailDTO } from '../types/clientList';
import { displayAnswer, groupFlatRowsByTopic } from '../components/profileform/ProfileFormTab';
import { VersionHistoryControl } from '../components/profileform/VersionHistoryControl';
import { usePageHeader } from '../contexts/PageHeaderContext';

/**
 * Advisor read-only Deep Dive page for ONE client — the per-client sibling of
 * `ClientRiskAssessmentPage` / `ClientSustainabilityPage` (Advisor 2.0 client
 * cards gain a Deep Dive button). Deep Dive is a PER_CLIENT, chat-captured
 * assessment: the page renders the selected client's answers grouped by the
 * question bank's topics, with a Version History picker. View-only.
 */
export function ClientDeepDivePage() {
  const { clientId } = useParams<{ clientId: string }>();
  const { setBreadcrumbItems, setBackTo } = usePageHeader();

  const [client, setClient] = useState<ClientDetailDTO | null>(null);
  const [schema, setSchema] = useState<FactFindSchema | null>(null);
  const [versions, setVersions] = useState<ProfileFormVersion[]>([]);
  const [selected, setSelected] = useState<ProfileFormVersion | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!clientId) return;
      setLoading(true);
      setError(null);
      try {
        const detail = await clientListApi.getClientDetail(clientId);
        const [s, all] = await Promise.all([
          profileFormApi.getSchema(detail.profileId, 'DEEP_DIVE'),
          profileFormApi.listVersions(detail.profileId, 'DEEP_DIVE', clientId),
        ]);
        if (cancelled) return;
        setClient(detail);
        setSchema(s);
        setVersions(all);
        setSelected(all[0] ?? null); // newest first
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load deep dive:', err);
        setError('The deep dive could not be loaded.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  useEffect(() => {
    if (client) {
      setBreadcrumbItems([
        { label: 'Relationships', to: '/relationships' },
        { label: `${client.firstName} ${client.lastName}`, to: `/clients/${client.id}` },
        { label: 'Deep Dive' },
      ]);
      setBackTo(`/clients/${client.id}`);
    }
    return () => {
      setBreadcrumbItems(null);
      setBackTo(null);
    };
  }, [client, setBreadcrumbItems, setBackTo]);

  const lastUpdated = versions[0]?.submittedAt
    ? new Date(versions[0].submittedAt).toLocaleDateString()
    : null;

  const rows = selected
    ? (schema?.questions ?? [])
        .map((q) => ({ q, value: selected.answers[q.id] }))
        .filter((r) => r.value != null && r.value !== '')
    : [];
  const groups = groupFlatRowsByTopic(rows);

  return (
    <div className="bg-page-bg p-lg min-h-screen overflow-auto">
      <div
        data-testid="client-deep-dive"
        className="flex flex-col flex-1 min-h-0 w-full max-w-page self-center mx-auto"
      >
        <div className="self-stretch rounded-lg bg-white shadow-card px-md py-lg flex flex-col gap-lg">
          <div className="flex flex-col items-start gap-sm sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-xs">
              <h1 className="text-black font-sans text-body-01 font-medium leading-normal m-0">
                Deep Dive
              </h1>
              {client && (
                <p
                  className="m-0 font-sans text-body-03 text-grey-01"
                  data-testid="deep-dive-client-name"
                >
                  {client.firstName} {client.lastName}
                </p>
              )}
            </div>
            <div className="flex items-center gap-md">
              {lastUpdated && (
                <span
                  className="font-sans text-body-03 text-grey-01"
                  data-testid="deep-dive-last-updated"
                >
                  Last Updated: {lastUpdated}
                </span>
              )}
              <VersionHistoryControl
                versions={versions}
                selectedVersionNo={selected?.versionNo ?? 0}
                onSelect={setSelected}
              />
            </div>
          </div>

          {loading ? (
            <div data-testid="deep-dive-loading" className="flex justify-center py-lg">
              <Spinner />
            </div>
          ) : error ? (
            <Alert variant="error" header="Error Loading Deep Dive">
              <p>{error}</p>
            </Alert>
          ) : selected ? (
            <>
              {selected.versionNo !== versions[0]?.versionNo && (
                <Alert variant="info" header={`Viewing version ${selected.versionNo}`}>
                  <p>
                    This is a historical version submitted on{' '}
                    {selected.submittedAt
                      ? new Date(selected.submittedAt).toLocaleDateString()
                      : 'an earlier date'}
                    . The latest is version {versions[0]?.versionNo}.
                  </p>
                </Alert>
              )}
              {groups.length === 0 && (
                <p className="m-0 font-sans text-body-03 text-grey-01">
                  No answers were provided.
                </p>
              )}
              {groups.map((group) => (
                <section
                  key={group.topic ?? 'flat'}
                  data-testid={group.topic ? `deep-dive-topic-${group.topic}` : undefined}
                >
                  {group.topic && (
                    <h2 className="m-0 mb-sm font-sans text-body-02 font-medium text-black">
                      {group.topic}
                    </h2>
                  )}
                  <dl className="m-0 grid sm:grid-cols-2 gap-x-md gap-y-sm">
                    {group.rows.map(({ q, value }) => (
                      <React.Fragment key={q.id}>
                        <dt className="font-sans text-body-03 text-grey-01">{q.label || q.id}</dt>
                        <dd className="m-0 font-sans text-body-03 text-black">
                          {displayAnswer(q, value)}
                        </dd>
                      </React.Fragment>
                    ))}
                  </dl>
                </section>
              ))}
            </>
          ) : (
            <Alert variant="info" header="No Deep Dive">
              <p>No deep dive available for this client.</p>
            </Alert>
          )}
        </div>
      </div>
    </div>
  );
}

export default ClientDeepDivePage;
